import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Trophy, Crown, Flame, Star, ShieldCheck, TrendingUp } from 'lucide-react';

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/leaderboard').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-400 text-sm">Cargando ranking…</div>;
  if (!data) return null;

  const month = new Date(data.month).toLocaleString('es-ES', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6 mb-8">
      {/* Coach of the Month */}
      {data.coach_of_month && (
        <div className="relative bg-gradient-to-br from-gold/15 via-card-steel to-card-steel border-2 border-gold rounded-lg p-6 overflow-hidden animate-glow-pulse">
          <div className="absolute -top-6 -right-6 opacity-10">
            <Crown className="w-40 h-40 text-gold" />
          </div>
          <div className="flex items-start gap-5 flex-wrap relative z-10">
            <div className="relative">
              {data.coach_of_month.photo_url ? (
                <img src={data.coach_of_month.photo_url} className="w-24 h-24 rounded-full border-4 border-gold object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-full border-4 border-gold bg-navy-deep" />
              )}
              <Crown className="absolute -top-3 -right-3 w-9 h-9 text-gold drop-shadow-lg" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">Coach of the Month · {month}</div>
              <div className="font-h uppercase text-4xl text-white flex items-center gap-2">
                {data.coach_of_month.display_name}
                <ShieldCheck className="w-6 h-6 text-gold" />
              </div>
              <div className="text-gold uppercase tracking-widest text-xs">{data.coach_of_month.credentials}</div>
              <div className="flex items-center gap-5 mt-3">
                <div className="text-center">
                  <div className="font-h text-3xl text-gold">{data.coach_of_month.sales_month}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-400">ventas mes</div>
                </div>
                <div className="text-center">
                  <div className="font-h text-3xl text-gold">${data.coach_of_month.revenue_month.toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-400">ingresos</div>
                </div>
                <div className="text-center">
                  <div className="font-h text-3xl text-gold flex items-center gap-1">{data.coach_of_month.rating?.toFixed(1)} <Star className="w-5 h-5 fill-gold" /></div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-400">rating</div>
                </div>
              </div>
            </div>
            <Link to={`/marketplace/coach/${data.coach_of_month.coach_id}`} data-testid="coach-of-month-link"
              className="px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light transition self-center">
              Ver tienda →
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top plays month */}
        <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
          <h3 className="font-h uppercase text-lg text-gold mb-3 flex items-center gap-2">
            <Flame className="w-5 h-5" /> Jugadas más vendidas · {month}
          </h3>
          {data.top_plays_month.length === 0 && <p className="text-gray-400 text-sm italic">Sin ventas este mes todavía.</p>}
          <div className="space-y-2">
            {data.top_plays_month.map((p, i) => (
              <Link key={p.id} to={`/marketplace/play/${p.id}`} data-testid={`top-month-${p.id}`}
                className="flex items-center gap-3 p-2 rounded border border-steel hover:border-gold/60 transition">
                <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold flex items-center justify-center font-h text-gold">
                  {i + 1}
                </div>
                {p.coach_photo && <img src={p.coach_photo} className="w-8 h-8 rounded-full border border-gold/50 object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-h uppercase text-white truncate">{p.name}</div>
                  <div className="text-xs text-gray-400 truncate">{p.coach_name} · {p.category}</div>
                </div>
                <div className="text-end">
                  <div className="text-gold font-mono">${p.price?.toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">{p.sales_month} ventas</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Top sellers */}
        <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
          <h3 className="font-h uppercase text-lg text-gold mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Top vendedores · {month}
          </h3>
          <div className="space-y-2">
            {data.top_sellers_month.map((c, i) => (
              <Link key={c.coach_id} to={`/marketplace/coach/${c.coach_id}`} data-testid={`top-seller-${c.coach_id}`}
                className="flex items-center gap-3 p-2 rounded border border-steel hover:border-gold/60 transition">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-h ${i === 0 ? 'bg-gold text-navy' : 'bg-gold/10 border border-gold/40 text-gold'}`}>
                  {i + 1}
                </div>
                {c.photo_url && <img src={c.photo_url} className="w-8 h-8 rounded-full border border-gold/50 object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-h uppercase text-white flex items-center gap-1 truncate">{c.display_name}<ShieldCheck className="w-3 h-3 text-gold flex-shrink-0" /></div>
                  <div className="text-xs text-gray-400 truncate">{c.credentials}</div>
                </div>
                <div className="text-end">
                  <div className="text-gold font-h text-lg">{c.sales_month}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">ventas</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* All-time + Top rated */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
          <h3 className="font-h uppercase text-lg text-gold mb-3 flex items-center gap-2">
            <Trophy className="w-5 h-5" /> Hall of Fame · All-time
          </h3>
          <div className="space-y-2">
            {data.top_plays_alltime.map((p, i) => (
              <Link key={p.id} to={`/marketplace/play/${p.id}`}
                className="flex items-center gap-3 p-2 rounded border border-steel hover:border-gold/60 transition">
                <div className="w-7 h-7 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center font-h text-gold text-sm">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-h uppercase text-white truncate">{p.name}</div>
                  <div className="text-xs text-gray-400 truncate">{p.coach_name}</div>
                </div>
                <div className="text-gold text-xs">{p.purchases_count || 0} ventas</div>
              </Link>
            ))}
          </div>
        </div>
        <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
          <h3 className="font-h uppercase text-lg text-gold mb-3 flex items-center gap-2">
            <Star className="w-5 h-5 fill-gold" /> Mejor valoradas
          </h3>
          {data.top_rated_plays.length === 0 && <p className="text-gray-400 text-sm italic">Aún sin reseñas suficientes.</p>}
          <div className="space-y-2">
            {data.top_rated_plays.map((p, i) => (
              <Link key={p.id} to={`/marketplace/play/${p.id}`}
                className="flex items-center gap-3 p-2 rounded border border-steel hover:border-gold/60 transition">
                <div className="w-7 h-7 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center font-h text-gold text-sm">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-h uppercase text-white truncate">{p.name}</div>
                  <div className="text-xs text-gray-400 truncate">{p.coach_name}</div>
                </div>
                <div className="flex items-center gap-1 text-gold">
                  <Star className="w-3 h-3 fill-gold" /> <span className="text-xs">{p.review_avg?.toFixed(1)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
